/**
 * "Which Customer K?" — choices with context, for when a name the owner used
 * matches more than one client (docs/intelligent-chat-answers-plan.md,
 * "Choices with context").
 *
 * 🚨 The rows are built by the SERVER, off the clients `resolveClient` matched
 * and one read of each. `ask_user` cannot carry them, because its option
 * detail is written by the model, and a postcode or a job count the model wrote
 * is exactly the thing this block exists not to guess.
 *
 * A tap sends an unambiguous reply AS THE OWNER ("Customer K, BS4 3ZZ"), which
 * the model then handles through the ordinary path with every check it already
 * has. The tap writes nothing. The tool result is told which reply means which
 * client id (`clientChoicesResultNote`), so the reply can be acted on without
 * a second lookup.
 *
 * Pure and directive-free: the route, the panel, the Mucka Bar and the
 * oversight grader all import it (anti-pattern #18).
 */

/** What `resolveClient` saw when a name matched several clients. */
export interface ClientMultiMatch {
  /** The name as the model passed it. */
  asked: string;
  /** The exact sentence `resolveClient` returned, so a caller can tell whether
   *  the tool passed it through to its result. */
  message: string;
  candidates: Array<{ id: string; name: string }>;
}

/** One matched client, with what the read found out about them. */
export interface ClientChoiceCandidate {
  id: string;
  name: string;
  /** From the client's primary current property, else their own address. */
  postcode: string | null;
  /** `clients.imported_from` — non-null means an importer wrote this record. */
  importedFrom: string | null;
  /** All of the client's jobs, or null when the seat may not count them. */
  jobCount: number | null;
  /** ISO timestamp of the latest job scheduled before now, or null. */
  lastJobAt: string | null;
}

export interface ClientChoiceRow {
  clientId: string;
  name: string;
  postcode: string | null;
  /** True when the record came in from another system. */
  imported: boolean;
  /** "March 2026", London. Null when there is no past job, or the seat may not see jobs. */
  lastJobMonth: string | null;
  /** Null when the seat may not count this client's jobs. */
  jobCount: number | null;
  /** What a tap says, as the owner. Unique within the block. */
  reply: string;
}

export interface ClientChoicesBlock {
  kind: 'client-choices';
  rows: ClientChoiceRow[];
  /** Matches beyond the rows shown. Never hidden silently. */
  more: number;
  /** The closing row: "Neither, someone new", "None of these…". */
  none: { label: string; reply: string };
}

/** Rows shown; the rest are counted. */
export const CLIENT_CHOICES_MAX = 3;

/**
 * Above this many matches there is no block. Reading a postcode and two job
 * figures for forty Smiths to show three of them is a page, not a choice, and
 * the sentence already asks the owner to say more.
 */
export const CLIENT_CHOICES_READ_MAX = 8;

/** Said on the tag. Names no provider: the owner knows which system they left. */
export const IMPORTED_TAG = 'From your old system';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "March 2026", in London. Built by hand so no engine's ICU can spell it differently (#41). */
export function londonMonthYear(iso: string): string | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric' })
    .formatToParts(new Date(t));
  const month = Number(parts.find(p => p.type === 'month')?.value);
  const year = parts.find(p => p.type === 'year')?.value;
  if (!month || !year) return null;
  return `${MONTHS[month - 1]} ${year}`;
}

function jobsWord(n: number): string {
  return n === 1 ? '1 job' : `${n} jobs`;
}

/**
 * The small print under a row's name. Every figure in it came off the read;
 * nothing is said about a field the seat may not see.
 */
export function clientChoiceDetail(row: Pick<ClientChoiceRow, 'lastJobMonth' | 'jobCount'>): string | null {
  if (row.jobCount == null) return null;
  if (row.jobCount === 0) return 'No jobs yet';
  if (row.lastJobMonth) return `Last job ${row.lastJobMonth} · ${jobsWord(row.jobCount)}`;
  // Every job is still ahead of them.
  return `${jobsWord(row.jobCount)}, none done yet`;
}

/**
 * A reply per row that no other row shares. Starts at name + postcode and adds
 * the next thing that tells them apart only when it has to, so the ordinary case
 * reads like a person: "Customer K, BS4 3ZZ".
 */
function uniqueReplies(rows: ReadonlyArray<Omit<ClientChoiceRow, 'reply'>>): string[] {
  const descriptors = rows.map(r => [
    r.postcode,
    r.imported ? 'from your old system' : null,
    r.lastJobMonth ? `last job ${r.lastJobMonth}` : null,
    r.jobCount != null ? `with ${jobsWord(r.jobCount)}` : null,
  ]);
  for (let k = 1; k <= 4; k++) {
    const replies = rows.map((r, i) => [r.name, ...descriptors[i].slice(0, k).filter(Boolean)].join(', '));
    if (new Set(replies.map(s => s.toLowerCase())).size === replies.length) return replies;
  }
  // Two records alike in everything we can read. Say which one by position,
  // which the result note maps to an id like any other reply.
  return rows.map((r, i) => [r.name, ...descriptors[i].filter(Boolean), `option ${i + 1}`].join(', '));
}

/**
 * The block, or null when there is nothing to choose between.
 *
 * `rankByJobs` orders by the latest job, most recent first, so the client the
 * owner most likely means is on top. Only for a seat that may see every job:
 * ordering by facts a seat cannot see would say something about them.
 */
export function buildClientChoicesBlock(
  candidates: readonly ClientChoiceCandidate[],
  options: { rankByJobs: boolean },
): ClientChoicesBlock | null {
  if (candidates.length < 2 || candidates.length > CLIENT_CHOICES_READ_MAX) return null;
  const ordered = candidates.map((c, i) => ({ c, i }));
  if (options.rankByJobs) {
    ordered.sort((a, b) => (b.c.lastJobAt ?? '').localeCompare(a.c.lastJobAt ?? '') || a.i - b.i);
  }
  const shown = ordered.slice(0, CLIENT_CHOICES_MAX).map(({ c }) => ({
    clientId: c.id,
    name: c.name,
    postcode: c.postcode?.trim() ? c.postcode.trim().toUpperCase() : null,
    imported: !!c.importedFrom,
    lastJobMonth: c.jobCount != null && c.lastJobAt ? londonMonthYear(c.lastJobAt) : null,
    jobCount: c.jobCount,
  }));
  const replies = uniqueReplies(shown);
  const more = candidates.length - shown.length;
  return {
    kind: 'client-choices',
    rows: shown.map((r, i) => ({ ...r, reply: replies[i] })),
    more,
    none: noneRow(shown.length, more),
  };
}

function noneRow(shown: number, more: number): ClientChoicesBlock['none'] {
  // With some not shown, "someone new" would be a guess: it may be one of them.
  if (more > 0) return { label: 'None of these', reply: 'None of those' };
  if (shown === 2) return { label: 'Neither, someone new', reply: "Neither, it's someone new" };
  return { label: 'None of these, someone new', reply: "None of those, it's someone new" };
}

/**
 * Appended to the tool RESULT when the block is on screen, so the model knows
 * the owner can tap, and which reply means which client. The ids are already in
 * `resolveClient`'s own sentence; this adds only the mapping.
 */
export function clientChoicesResultNote(block: ClientChoicesBlock): string {
  const mapping = block.rows.map(r => `"${r.reply}" = client ${r.clientId}`).join('; ');
  const more = block.more ? ` ${block.more} more with that name are not shown.` : '';
  return `These ${block.rows.length} are on screen as choices the owner can tap, with postcode and job history.${more} `
    + `Ask which one in a sentence; do not read the choices out. A tap replies with one of: ${mapping}. `
    + `Use that client id. "${block.none.reply}" means none of them.`;
}

/**
 * One line for the oversight transcript: how many choices and what they could
 * be told apart by. Nothing a customer could be identified by.
 */
export function summariseClientChoicesBlock(block: ClientChoicesBlock): string {
  const total = block.rows.length + block.more;
  const withPostcode = block.rows.filter(r => r.postcode).length;
  const imported = block.rows.filter(r => r.imported).length;
  const counted = block.rows.some(r => r.jobCount != null);
  const facts = [
    `${withPostcode} of ${block.rows.length} with a postcode`,
    imported ? `${imported} imported` : null,
    counted ? 'job history shown' : 'no job history (seat)',
  ].filter(Boolean).join(', ');
  return `[client-choices: ${total} matching client${total === 1 ? '' : 's'}, ${block.rows.length} shown${block.more ? `, ${block.more} more not shown` : ''}, ${facts}, tap to pick]`;
}
